// Helpers for the expense form's "Scan receipt" flow. The original file always stays the attachment (the server converts
// HEIC/HEIF/TIFF/AVIF to JPEG when the expense is saved); only the copy sent for text recognition may be downscaled here.
// The browser never rejects a file by type or extension: the server detects the type from the bytes and decides.

export const SCAN_MAX_SIDE = 3000;
export const SCAN_JPEG_QUALITY = 0.85;

// File input accept list (also used for the mobile camera input, which only needs image/*).
export const RECEIPT_ACCEPT = 'image/*,.heic,.heif,.avif,.tif,.tiff,.bmp,.gif,.webp,.jpg,.jpeg,.jfif,.png,application/pdf';

const NATIVE_EXT = /\.(jpe?g|jfif|png|webp|gif|bmp)$/i;
const NATIVE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/bmp'];

// Formats the browser is not asked to decode at all: they are sent as they are and the server downscales / converts them.
// (Some browsers cannot decode them, others decode a 24 MP phone photo slowly or with colour-space surprises.)
export const isServerDecodeOnly = (file) =>
    /^image\/(hei[cf]|tiff|avif)$/i.test(file?.type || '') || /\.(heic|heif|tiff?|avif)$/i.test(file?.name || '');

export const isHeic = (file) => /^image\/hei[cf]$/i.test(file?.type || '') || /\.(heic|heif)$/i.test(file?.name || '');

// Formats every browser displays: their photo preview is an object URL of the file itself. Everything else (HEIC, HEIF,
// TIFF, AVIF, or a file whose type and name say nothing) gets its preview from the server's JPEG.
export const isNativelyDisplayable = (file) => NATIVE_TYPES.includes(file?.type) || (!file?.type && NATIVE_EXT.test(file?.name || ''));

export const scaledSize = (width, height, maxSide = SCAN_MAX_SIDE) => {
    const longSide = Math.max(width, height);
    if (!longSide || longSide <= maxSide) return { width, height, scaled: false };
    const ratio = maxSide / longSide;
    return { width: Math.round(width * ratio), height: Math.round(height * ratio), scaled: true };
};

export const readAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
});

const readHead = (file, n) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result));
    reader.onerror = () => resolve(new Uint8Array(0));
    reader.readAsArrayBuffer(file.slice(0, n));
});

// A PDF is recognised by its type, its name or (when both are missing or wrong) its first bytes "%PDF-".
export const isPdfFile = async (file) => {
    if (file?.type === 'application/pdf' || /\.pdf$/i.test(file?.name || '')) return true;
    const head = await readHead(file, 5);
    return head.length === 5 && String.fromCharCode(...head) === '%PDF-';
};

const DECODE_TIMEOUT_MS = 10000;

const loadImage = (url) => new Promise((resolve, reject) => {
    const img = new Image();
    const timer = setTimeout(() => reject(new Error('decode timeout')), DECODE_TIMEOUT_MS);   // a decode that never finishes must not stall the scan
    img.onload = () => { clearTimeout(timer); resolve(img); };
    img.onerror = (e) => { clearTimeout(timer); reject(e); };
    img.src = url;
});

// Data URL to send for extraction: the original bytes, unless the browser can decode the image and its long side exceeds
// SCAN_MAX_SIDE, in which case a JPEG (quality 0.85) of the downscaled image. If the browser cannot decode the file (HEIC
// outside Safari, TIFF, AVIF ...) or anything goes wrong, the downscale is skipped and the original bytes are sent.
export const prepareImageForScan = async (file) => {
    if (isServerDecodeOnly(file)) return readAsDataUrl(file);
    let objectUrl = null;
    try {
        objectUrl = URL.createObjectURL(file);
        const img = await loadImage(objectUrl);
        const size = scaledSize(img.naturalWidth, img.naturalHeight);
        if (!size.scaled) return await readAsDataUrl(file);
        const canvas = document.createElement('canvas');
        canvas.width = size.width;
        canvas.height = size.height;
        canvas.getContext('2d').drawImage(img, 0, 0, size.width, size.height);
        return canvas.toDataURL('image/jpeg', SCAN_JPEG_QUALITY);
    } catch (e) {
        return readAsDataUrl(file);
    } finally {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
    }
};
